import React, { useState, useEffect } from "react";
import { X, Smartphone, ArrowDownToLine, Info, Chrome } from "lucide-react";
import { useLanguage } from "@/context/LanguageContext";

export default function PWAInstallBanner() {
  const { language } = useLanguage();
  const [isVisible, setIsVisible] = useState(false);
  const [showInstructions, setShowInstructions] = useState(false);
  const [platform, setPlatform] = useState("unknown");
  const [isInApp, setIsInApp] = useState(false);

  useEffect(() => {
    // 1. Don't show if already installed (standalone mode)
    const isStandalone =
      window.matchMedia("(display-mode: standalone)").matches ||
      window.navigator.standalone === true;

    if (isStandalone) return;

    // 2. Don't show if dismissed in this session
    const isDismissed = sessionStorage.getItem("pwa-banner-dismissed");
    if (isDismissed) return;

    // 3. Detect Platform & In-App Browser
    const ua = navigator.userAgent || navigator.vendor || window.opera;
    const isIOS = /iPad|iPhone|iPod/.test(ua) && !window.MSStream;
    const isAndroid = /Android/i.test(ua);

    // ✅ CRITICAL: Only show on mobile devices — never on desktop
    if (!isIOS && !isAndroid) return;

    // Simple In-App Browser detection (Facebook, Instagram, WhatsApp, Line, WeChat, etc.)
    const isInAppBrowser =
      /FBAN|FBAV|Instagram|WhatsApp|Line|MicroMessenger|WebView/i.test(ua);

    setIsInApp(isInAppBrowser);
    if (isIOS) {
      setPlatform("ios");
    } else if (isAndroid) {
      setPlatform("android");
    }

    // Delay showing the banner slightly to let user settle
    const timer = setTimeout(() => {
      setIsVisible(true);
    }, 3000);

    return () => clearTimeout(timer);
  }, []);


  useEffect(() => {
    const handleShowInstructions = (e) => {
      const targetPlatform = e.detail?.platform || "android";
      setPlatform(targetPlatform);
      setShowInstructions(true);
    };
    window.addEventListener("show-pwa-instructions", handleShowInstructions);
    return () => window.removeEventListener("show-pwa-instructions", handleShowInstructions);
  }, []);

  const handleInstallClick = () => {
    if (window._pwaInstallPrompt) {
      window._pwaInstallPrompt.prompt();
      window._pwaInstallPrompt.userChoice.then((choiceResult) => {
        if (choiceResult.outcome === "accepted") {
          setIsVisible(false);
        }
      });
    } else {
      // No native prompt available -> Show manual instructions
      setShowInstructions(true);
    }
  };

  const handleDismiss = () => {
    sessionStorage.setItem("pwa-banner-dismissed", "true");
    setIsVisible(false);
  };

  return (
    <>
      {/* Bottom Floating Banner */}
      {isVisible && (
        <div className="fixed bottom-4 inset-x-4 md:inset-x-auto md:right-4 md:max-w-md bg-[#6B1A24] text-white p-4 rounded-2xl shadow-2xl border border-white/10 z-50 animate-slide-up flex flex-col gap-3 backdrop-blur-md bg-opacity-95">
          <div className="flex items-start justify-between gap-3">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 rounded-xl bg-white p-1 flex-shrink-0 flex items-center justify-center shadow-inner">
                <img src="/logo.png" alt="Logo" className="w-full h-full object-contain" />
              </div>
              <div className="text-left">
                <h4 className="font-bold text-sm leading-tight text-amber-100">
                  {language === "hi" ? "झारखंड जोड़ी ऐप" : "Jharkhand Jodi App"}
                </h4>
                <p className="text-xs text-white/80 leading-snug mt-0.5">
                  {language === "hi" 
                    ? "तेज़ और बेहतर अनुभव के लिए हमारे ऐप को अपने फ़ोन पर इंस्टॉल करें।"
                    : "Install our app on your phone for a faster & premium experience."}
                </p>
              </div>
            </div>
            <button 
              onClick={handleDismiss} 
              className="text-white/60 hover:text-white bg-white/10 hover:bg-white/20 p-1.5 rounded-full transition-all"
            >
              <X className="w-4 h-4" />
            </button>
          </div>

          <div className="flex items-center gap-2 mt-1">
            <button
              onClick={handleInstallClick}
              className="flex-grow bg-amber-500 hover:bg-amber-400 text-amber-950 font-bold text-xs py-2.5 px-4 rounded-xl transition-all shadow-md flex items-center justify-center gap-1.5 hover:scale-[1.02]"
            >
              <ArrowDownToLine className="w-4 h-4" />
              {language === "hi" ? "ऐप इंस्टॉल करें" : "Install App"}
            </button>
            <button
              onClick={() => setShowInstructions(true)}
              className="bg-white/10 hover:bg-white/20 text-white font-medium text-xs py-2.5 px-3 rounded-xl transition-all flex items-center gap-1"
            >
              <Info className="w-3.5 h-3.5" />
              {language === "hi" ? "कैसे करें?" : "Guide"}
            </button>
          </div>
        </div>
      )}

      {/* Manual Instructions Modal Overlay */}
      {showInstructions && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4 animate-fade-in">
          <div className="bg-white text-slate-800 rounded-3xl p-6 max-w-sm w-full shadow-2xl relative border border-slate-100 text-left space-y-4">
            <button
              onClick={() => setShowInstructions(false)}
              className="absolute top-4 right-4 text-slate-400 hover:text-slate-600 bg-slate-100 hover:bg-slate-200 p-1.5 rounded-full transition-all"
            >
              <X className="w-4 h-4" />
            </button>

            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-2xl bg-[#6B1A24]/10 text-[#6B1A24] flex items-center justify-center">
                <Smartphone className="w-5 h-5" />
              </div>
              <h3 className="text-base font-black text-slate-900 leading-tight">
                {language === "hi" ? "ऐप कैसे इंस्टॉल करें" : "How to Install App"}
              </h3>
            </div>

            <div className="divide-y divide-slate-100 text-xs leading-relaxed space-y-3 pt-2">
              {/* In-app Browser Notice */}
              {isInApp && (
                <div className="bg-amber-50 border border-amber-100 text-amber-800 p-3 rounded-2xl mb-2 flex items-start gap-2">
                  <Info className="w-4 h-4 text-amber-600 flex-shrink-0 mt-0.5" />
                  <div>
                    <span className="font-bold">{language === "hi" ? "इन-ऐप ब्राउज़र चेतावनी:" : "In-App Browser:"}</span>
                    <p className="mt-1">
                      {language === "hi"
                        ? "आप अभी व्हाट्सएप/सोशल मीडिया ब्राउज़र के अंदर हैं। इसे इंस्टॉल करने के लिए ऊपर दाईं ओर 3 डॉट्स पर टैप करें और 'Open in Chrome/Safari' चुनें।"
                        : "You are currently inside an in-app browser. Tap the 3 dots in the top right and select 'Open in Chrome/Safari' to enable installation."}
                    </p>
                  </div>
                </div>
              )}

              {/* Android Instructions */}
              {platform === "android" && (
                <div className="pt-3 flex gap-3">
                  <div className="w-6 h-6 rounded-full bg-emerald-100 text-emerald-700 flex items-center justify-center font-bold text-xs flex-shrink-0">
                    1
                  </div>
                  <div>
                    <span className="font-bold text-slate-900 flex items-center gap-1">
                      <Chrome className="w-3.5 h-3.5 text-emerald-600" />
                      {language === "hi" ? "Android (Chrome) के लिए:" : "For Android (Chrome):"}
                    </span>
                    <p className="text-slate-500 mt-1">
                      {language === "hi"
                        ? "1. Chrome ब्राउज़र में खोलें।\n2. ऊपर दाईं ओर 3 डॉट्स (⋮) पर क्लिक करें।\n3. 'Install app' या 'Add to Home screen' चुनें।"
                        : "1. Open in Chrome browser.\n2. Tap the three dots (⋮) in the top-right corner.\n3. Tap 'Install app' or 'Add to Home screen'."}
                    </p>
                  </div>
                </div>
              )}

              {/* iOS Instructions */}
              {platform === "ios" && (
                <div className="pt-3 flex gap-3">
                  <div className="w-6 h-6 rounded-full bg-blue-100 text-blue-700 flex items-center justify-center font-bold text-xs flex-shrink-0">
                    1
                  </div>
                  <div>
                    <span className="font-bold text-slate-900">
                      {language === "hi" ? "iPhone (Safari) के लिए:" : "For iPhone (Safari):"}
                    </span>
                    <p className="text-slate-500 mt-1">
                      {language === "hi"
                        ? "1. Safari ब्राउज़र में खोलें।\n2. नीचे 'Share' बटन (तीर का निशान) पर टैप करें।\n3. नीचे स्क्रॉल करें और 'Add to Home Screen' चुनें।"
                        : "1. Open in Safari browser.\n2. Tap the 'Share' icon (square with arrow) at the bottom.\n3. Scroll down and tap 'Add to Home Screen'."}
                    </p>
                  </div>
                </div>
              )}
            </div>

            <button
              onClick={() => setShowInstructions(false)}
              className="w-full bg-slate-900 hover:bg-slate-800 text-white font-bold text-xs py-3 rounded-xl transition-all mt-4"
            >
              {language === "hi" ? "समझ गया" : "Got it"}
            </button>
          </div>
        </div>
      )}
    </>
  );
}
